import Axios from "axios";
import { API_ROOT } from "../../config";
import {
  getIdToken,
  loginRedirect,
  logoutRedirect,
  handleRedirectPromise,
  isMsalConfigured,
  getMsalConfigError
} from "../../auth/msal";

const ax = Axios.create({
  baseURL: API_ROOT.osbod_salary,
  timeout: 60000
});

function readStoredUser() {
  try {
    var raw = localStorage.getItem("user");
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

export default {
  state: {
    access: localStorage.getItem("access") === "true",
    user: readStoredUser(),
    idToken: localStorage.getItem("idToken") || null,
    loginError: null,
    loginLoading: false
  },
  mutations: {
    LOGIN(state, payload) {
      state.access = true;
      state.user = payload.data;
      state.idToken = payload.idToken || state.idToken;
      state.loginError = null;
      localStorage.setItem("user", JSON.stringify(payload.data));
      localStorage.setItem("access", "true");
      if (payload.idToken) {
        localStorage.setItem("idToken", payload.idToken);
      }
    },
    LOGOUT(state) {
      state.access = false;
      state.user = null;
      state.idToken = null;
      state.loginError = null;
      localStorage.removeItem("user");
      localStorage.removeItem("access");
      localStorage.removeItem("idToken");
    },
    SET_LOGIN_ERROR(state, message) {
      state.loginError = message || null;
    },
    SET_LOGIN_LOADING(state, value) {
      state.loginLoading = !!value;
    }
  },
  getters: {
    getLoggedin(state) {
      return state.access;
    },
    getUser(state) {
      return state.user;
    },
    getLoginError(state) {
      return state.loginError;
    },
    getLoginLoading(state) {
      return state.loginLoading;
    },
    getIdToken(state) {
      return state.idToken;
    }
  },
  actions: {
    async exchangeIdToken(context, idToken) {
      try {
        var response = await ax.post("/public/student_login", {
          idToken: idToken
        });
        var body = response.data || {};
        if (!body.success) {
          throw new Error(body.msg || "Inloggen is niet gelukt!");
        }
        context.commit("LOGIN", { data: body.data, idToken: idToken });
        return body.data;
      } catch (err) {
        var data = err && err.response ? err.response.data : null;
        var msg =
          (data && (data.msg || data.message)) ||
          (err && err.message) ||
          "Inloggen is niet gelukt!";
        throw new Error(msg);
      }
    },

    async loginWithEntra(context) {
      context.commit("SET_LOGIN_ERROR", null);
      if (!isMsalConfigured()) {
        context.commit("SET_LOGIN_ERROR", getMsalConfigError());
        return;
      }
      context.commit("SET_LOGIN_LOADING", true);
      try {
        await loginRedirect();
      } catch (err) {
        context.commit(
          "SET_LOGIN_ERROR",
          (err && err.message) || "Microsoft-login mislukt"
        );
        context.commit("SET_LOGIN_LOADING", false);
      }
    },

    async completeEntraRedirect(context) {
      if (!isMsalConfigured()) {
        return false;
      }
      context.commit("SET_LOGIN_LOADING", true);
      context.commit("SET_LOGIN_ERROR", null);
      try {
        if (typeof window !== "undefined" && window.__msalRedirectError) {
          var bootstrapErr = window.__msalRedirectError;
          window.__msalRedirectError = null;
          throw new Error(bootstrapErr);
        }

        var result = await handleRedirectPromise();
        var idToken = result && result.idToken ? result.idToken : null;
        if (!idToken) {
          idToken = await getIdToken();
        }
        if (!idToken) {
          context.commit("SET_LOGIN_LOADING", false);
          return false;
        }
        await context.dispatch("exchangeIdToken", idToken);
        context.commit("SET_LOGIN_LOADING", false);
        return true;
      } catch (err) {
        console.error("Student Entra login failed:", err);
        var msg =
          (err && err.response && err.response.data && err.response.data.msg) ||
          (err && err.message) ||
          "Inloggen is niet gelukt!";
        context.commit("SET_LOGIN_ERROR", msg);
        context.commit("LOGOUT");
        context.commit("SET_LOGIN_LOADING", false);
        return false;
      }
    },

    async restoreSession(context) {
      if (!isMsalConfigured()) {
        return false;
      }
      var storedToken = localStorage.getItem("idToken");
      if (!storedToken && localStorage.getItem("access") !== "true") {
        return false;
      }
      try {
        var idToken = (await getIdToken()) || storedToken;
        if (!idToken) {
          context.commit("LOGOUT");
          return false;
        }
        var response = await ax.get("/public/student_me", {
          headers: { Authorization: "Bearer " + idToken }
        });
        var body = response.data || {};
        if (!body.success) {
          context.commit("LOGOUT");
          return false;
        }
        context.commit("LOGIN", { data: body.data, idToken: idToken });
        return true;
      } catch (err) {
        console.error("Student session restore failed:", err);
        context.commit("LOGOUT");
        return false;
      }
    },

    async fetchStudentFinance(context) {
      var storedToken = localStorage.getItem("idToken") || context.state.idToken;
      var idToken = null;
      try {
        idToken = (await getIdToken()) || storedToken;
      } catch (err) {
        idToken = storedToken;
      }
      if (!idToken) {
        throw new Error("Niet geautoriseerd");
      }

      try {
        var response = await ax.get("/public/student_finance", {
          headers: { Authorization: "Bearer " + idToken }
        });
        var body = response.data || {};
        if (!body.success) {
          throw new Error(
            body.msg || "Financiële gegevens konden niet worden geladen."
          );
        }
        if (idToken !== context.state.idToken) {
          localStorage.setItem("idToken", idToken);
          context.state.idToken = idToken;
        }
        return body.data || { student_id: null, transactions: [] };
      } catch (err) {
        var msg =
          (err && err.response && err.response.data && err.response.data.msg) ||
          (err && err.message) ||
          "Financiële gegevens konden niet worden geladen.";
        throw new Error(msg);
      }
    },

    async getStudentAuthToken(context) {
      var storedToken = localStorage.getItem("idToken") || context.state.idToken;
      var idToken = null;
      try {
        idToken = (await getIdToken()) || storedToken;
      } catch (err) {
        idToken = storedToken;
      }
      if (!idToken) {
        throw new Error("Niet geautoriseerd");
      }
      if (idToken !== context.state.idToken) {
        localStorage.setItem("idToken", idToken);
        context.state.idToken = idToken;
      }
      return idToken;
    },

    async fetchStudentOvertakeClasses(context) {
      try {
        var idToken = await context.dispatch("getStudentAuthToken");
        var response = await ax.get("/public/student_overtake_class", {
          headers: { Authorization: "Bearer " + idToken }
        });
        var body = response.data || {};
        if (!body.success) {
          throw new Error(body.msg || "Inhaallessen konden niet worden geladen.");
        }
        return body.data || [];
      } catch (err) {
        var msg =
          (err && err.response && err.response.data && err.response.data.msg) ||
          (err && err.message) ||
          "Inhaallessen konden niet worden geladen.";
        throw new Error(msg);
      }
    },

    async createStudentOvertakeClass(context, payload) {
      try {
        var idToken = await context.dispatch("getStudentAuthToken");
        var response = await ax.post(
          "/public/student_overtake_class",
          payload || {},
          {
            headers: { Authorization: "Bearer " + idToken }
          }
        );
        var body = response.data || {};
        if (!body.success) {
          throw new Error(body.msg || "Aanmelden van inhaalles is mislukt.");
        }
        return body;
      } catch (err) {
        var msg =
          (err && err.response && err.response.data && err.response.data.msg) ||
          (err && err.message) ||
          "Aanmelden van inhaalles is mislukt.";
        throw new Error(msg);
      }
    },

    async logout(context) {
      context.commit("LOGOUT");
      try {
        await logoutRedirect();
      } catch (err) {
        // local logout already done
      }
    }
  }
};
